import { useState } from "react";
import type { ProfileListRow } from "@amber/profiles/contracts";
import {
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  ReasonField,
  useReason,
  useToast,
  useCommandMutation,
} from "@amber/ui";
import { planCommands, AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";

function ResumeText({ catchUp, setCatchUp }: { catchUp: boolean; setCatchUp: (next: boolean) => void }) {
  return (
    <>
      <DialogText>
        Scans start again with the current plan. On each source that wasn&apos;t kept running by another plan, the
        paused interval becomes a gap “Not scanned while paused”.
      </DialogText>
      <label className="mt-3 flex items-start gap-2 text-ui">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={catchUp}
          onChange={(e) => {
            setCatchUp(e.target.checked);
          }}
        />
        <span>
          <b className="font-strong">Catch up on the paused interval</b>
          <small className="block text-sm text-muted">
            {catchUp
              ? "Queues catch-up scans behind fresh monitoring. It doesn't claim complete coverage."
              : "Without catch-up each gap needs someone and creates a task."}
          </small>
        </span>
      </label>
    </>
  );
}

export function PersonPauseDialog({ person, onClose }: { person: ProfileListRow; onClose: () => void }) {
  const toast = useToast();
  const pausing = person.lifecycle_state === "active";
  const reason = useReason();
  const [catchUp, setCatchUp] = useState(true);
  const mutation = useCommandMutation({
    run: (csrf, text: string) =>
      pausing
        ? planCommands.pausePerson(person.id, { reason: text }, csrf)
        : planCommands.resumePerson(person.id, { reason: text, catch_up: catchUp }, csrf),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
    onSuccess: () => {
      toast.show(pausing ? `${person.name} paused` : `${person.name} resumed`);
      onClose();
    },
  });
  const action = pausing ? "Pause" : "Resume";
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={pausing ? `Pause ${person.name}` : `Resume ${person.name}`}
      onSubmit={() => {
        if (reason.submit()) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter onClose={onClose} confirm={action} pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      {pausing ? (
        <DialogText>
          Scans requested only for {person.name} stop until someone resumes them; there is no automatic end. Nothing is
          deleted and the plan is kept. Shared sources keep running for the other people and plans that need them.
        </DialogText>
      ) : (
        <ResumeText catchUp={catchUp} setCatchUp={setCatchUp} />
      )}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} />
    </Dialog>
  );
}
